import { createSelector } from 'reselect'
import { RootState } from '../reducers/persistedReducer'
import { CellUtilities, CellDataTypes } from '@cluster/web-components'
import { initialChannelSettings } from '../reducers/channelSettings';
import produce, { enableES5} from "immer"
import { initialDecoratorsSettings } from '../reducers/cellDecorators';
import { DrawerTypes } from '../reducers/drawer';
import { CellLayoutType, CellWithLayoutType, LAYOUT_COLUMN_GAP } from '../../Channel/ChannelDataTypes';
import { ClusterApi } from '@cluster/web-components';

enableES5();

const getCells = (state: RootState) => state.cells.data;

const getChannelSettings = (state: RootState) => state.channelSettings;

export const selectLayoutData = (state: RootState) => state.channelLayout.data;

export const selectChannels = (state: RootState) => state.channels.data;

export const selectTheme = (state: RootState) => state.system.theme;

export const selectLayoutUpdateAvailable = (state: RootState) => state.channelLayout.updateAvailable;

export const selectChannelPopup = (state: RootState) => state.channelLayout.channelPopup;

export const selectActiveChannel = (state: RootState) => state.channels.activeChannel;

export const selectChannelFilters = (state: RootState) => state.channels.activeChannel.filters??{};

export const selectFavoriteCells = (state: RootState) => state.user.favoriteCells;

export const selectLoggedIn = (state: RootState) => state?.user?.loggedIn??false;

export const selectUser = (state: RootState) => state.user.user;

export const selectToken = (state: RootState) => state.user.token;

export const selectAuthSourceToken = (state: RootState) => state.user.authSourceToken;

export const selectIsUserGuest = (state: RootState) => state.user.user?.id === 0

export const selectMessageClientStatus = (state: RootState) => state.system.messageClientStatus;

export const selectMarketMovers = (state: RootState) => state.marketMovers.data.items;

export const selectModal = (state: RootState) => state.modal;

export const selectLocationType = (state: RootState) => state.location.type;

export const selectPage = (state: RootState) => state.page;

export const selectIsMobile = (state: RootState) => state.system.isMobile;

export const selectDrawer = (state: RootState) => selectIsMobile(state) && state.drawer === DrawerTypes.none ? DrawerTypes.events : state.drawer;

export const selectDesktopDrawerOpen = (state: RootState) => !selectIsMobile(state) && state.drawer !== DrawerTypes.none

export const selectMobileMenuOpen = (state: RootState) => selectIsMobile(state) && state.system.mobileMenuOpen;

export const selectTrainingVideos = (state: RootState) => state.trainingVideos.data.items;

export const selectFilterMenuOpen = (state: RootState) => state.system.filterMenuOpen;

export const selectDeviceId = (state: RootState) => state.system.deviceId;

export const selectChannelById = createSelector(
  selectChannels,
  (state: RootState, channelId: number) => channelId,
  (channels, channelId) => channels.find(channel => channel.id === channelId)
)

export const selectChannelIds = createSelector(
  selectChannels,
  (channels) => channels.map(channel => channel.id)
)

export const selectLayoutType = createSelector(
  selectLayoutData,
  (channelLayout) => channelLayout.layoutConfig?.type ?? "grid"
)

export const selectCellLayoutIds = createSelector(
  selectLayoutData,
  (channelLayout) => {
    if (channelLayout.layoutConfig?.type === "columns") {
      return (channelLayout.layout as ClusterApi.ComputedClusterCell[][]).reduce((ids: number[], cells) => {
        return ids.concat(cells.map(cellLayout => cellLayout.cell))
      }, [])
    } else {
      return (channelLayout.layout as ClusterApi.ComputedClusterCell[]).map(cellLayout => cellLayout.cell)
    }
  }
)

export const selectLayoutColumnDims = createSelector(
  selectLayoutData,
  (channelLayout) => {
    return channelLayout.layoutConfig?.columnLayouts?.reduce((dimensions: {gridWidth: number, gridHeight: number, width: number}[], config) => {
      dimensions.push({
        gridWidth: config.width,
        gridHeight: config.height,
        width: config.width * CellDataTypes.CELL_WIDTH + (config.width-1) * CellDataTypes.CELL_GAP});
      return dimensions;
    }, []) ?? [];
  }
)

export const selectCellLayouts = createSelector(
  selectLayoutData,
  selectLayoutColumnDims,
  (channelLayout, columnDims) => {
    let cellLayouts: CellLayoutType[] = [];

    // Storage for all layout cells
    let flattenedLayoutCells: Array<ClusterApi.ComputedClusterCell & {column?: number}> = [];

    if (channelLayout.layoutConfig?.type === "columns") {
      // Get column cells, storing column index as a property for further calculation
      flattenedLayoutCells = (channelLayout.layout as ClusterApi.ComputedClusterCell[][]).reduce((layoutCells: Array<ClusterApi.ComputedClusterCell & {column?: number}>, columnData, columnNum) => {
        for (const cellLayoutData of columnData) {
          layoutCells.push({
            ...cellLayoutData,
            column: columnNum,
          })
        }

        return layoutCells;
      }, [])
    } else {
      // Get grid cells
      flattenedLayoutCells = (channelLayout.layout as ClusterApi.ComputedClusterCell[]);
    }

    for (const cellLayoutData of flattenedLayoutCells) {
      let positionData, x, y, column;

      const {width, height} = CellUtilities.getCellDimensions(cellLayoutData.size);

      if (typeof cellLayoutData.column !== "undefined") {
        positionData = CellUtilities.calcLayoutColumnsPosition(columnDims.map(dims => dims.gridWidth), cellLayoutData.column, cellLayoutData.position);
        x = (positionData.posCol-1-cellLayoutData.column) * (CellDataTypes.CELL_GAP + CellDataTypes.CELL_WIDTH) + cellLayoutData.column * (CellDataTypes.CELL_GAP + LAYOUT_COLUMN_GAP);
        column = cellLayoutData.column;
      } else {
        positionData = CellUtilities.calcLayoutGridPosition(cellLayoutData.position, channelLayout.size!);
        x = (positionData.posCol-1) * (CellDataTypes.CELL_GAP + CellDataTypes.CELL_WIDTH);
      }

      y = (positionData.posRow-1) * (CellDataTypes.CELL_HEIGHT + CellDataTypes.CELL_GAP);

      cellLayouts.push({
        id: cellLayoutData.cell,
        posCol: positionData.posCol,
        posRow: positionData.posRow,
        x,
        y,
        column,
        width,
        height,
        isCentroid: channelLayout.centroidCellId === cellLayoutData.cell
      });
    }

    return cellLayouts;
  }
)

export const selectCellLayoutById = createSelector(
  selectCellLayouts,
  (state: RootState, cellId: number | null) => {
    return cellId;
  },
  (cellLayouts, cellId) => cellLayouts.find(cellLayout => cellLayout.id === cellId)
);

export const selectLayoutCells = createSelector(
  selectCellLayouts,
  getCells,
  (cellLayouts, cells) => {
    let layoutCells = produce((cells as CellWithLayoutType[]), (draft) => {
      return draft.reduce((cells, cell) => {
        const cellLayoutData = cellLayouts.find(cellLayout => cellLayout.id === cell.id);

        if (cellLayoutData) {

          cells.push({
            ...cell,
            ...cellLayoutData
          });
        }

        return cells;

      }, [] as CellWithLayoutType[]);

    });

    return layoutCells;
  }
)

export const selectLayoutCellById = createSelector(
  selectLayoutCells,
  (state: RootState, cellId: number | null) => cellId,
  (layoutCells, cellId) => {

    return layoutCells.find(cell => cell.id === cellId);
  }
)

export const selectChannelCellExists = createSelector(
  selectLayoutCellById,
  (cell) => cell ? true : false
)

export const selectCurrentChannel = createSelector(
  selectChannels,
  selectActiveChannel,
  (channels, activeChannel) => {
    if (activeChannel?.channelId) {
      return channels.find(channel => channel.id === activeChannel?.channelId);
    }
  }
)

export const selectCurrentChannelId = createSelector(
  selectActiveChannel,
  (channel) => {
    return channel?.channelId;
  }
)

export const selectChannelSettings = createSelector(
  getChannelSettings,
  (state: RootState, channelId: number | undefined) => channelId,
  (settingsList, channelId) => {
    return (channelId && settingsList[channelId]) ? settingsList[channelId] : initialChannelSettings;
  }
)

export const selectCurrentChannelSettings = createSelector(
  getChannelSettings,
  selectCurrentChannelId,
  (settingsList, channelId) => {
    return (channelId && settingsList[channelId]) ? settingsList[channelId] : initialChannelSettings;
  }
)

export const selectCellIsFavorite = createSelector(
  selectFavoriteCells,
  (state: RootState, cellId: number) => cellId,
  (favorites, cellId) => {
    return favorites.includes(cellId);
  }
)

export const selectFavoriteLayoutCells = createSelector(
  selectLayoutCells, selectFavoriteCells,
  (layoutCells, favoriteCellIds) => {
    return layoutCells.filter(cell => favoriteCellIds.includes(cell.id));
  }
)

export const selectCellDecorators = createSelector(
  (state: RootState, cellId: number) => { return { decoratorList: state.cellDecorators, cellId }},
  ({decoratorList, cellId}) => {
    if (decoratorList[cellId]) {
      return decoratorList[cellId];
    } else {
      return initialDecoratorsSettings;
    }
  }
)

export const selectLayoutIsStatic = createSelector(
  selectLayoutData,
  (channelLayout) => {
    return !!channelLayout.static;
  }
)